import { t } from '@/i18n';
import { ColorPicker } from '@/components/common/ColorPicker';
import { IconPicker } from '@/components/common/IconPicker';
import { PresetTextField } from '@/components/common/PresetTextField';

interface IndicatorSettingsProps {
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
}

export function IndicatorSettings({ config, onConfigChange }: IndicatorSettingsProps) {
  const handleChange = (field: string, value: unknown) => {
    onConfigChange({ ...config, [field]: value });
  };

  const numberValue = (field: string): string =>
    config[field] != null ? String(config[field]) : '';

  return (
    <div>
      <h5>{t('dashboard.widget.indicator.section.mainSettings')}</h5>

      <div className="mb-3">
        <label className="form-label">{t('dashboard.widget.indicator.field.label')}</label>
        <input
          type="text"
          className="form-control"
          data-testid="indicator-settings-label"
          value={(config.label as string) ?? ''}
          onChange={(e) => handleChange('label', e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">{t('dashboard.widget.indicator.field.description')}</label>
        <textarea
          className="form-control"
          data-testid="indicator-settings-description"
          placeholder={t('dashboard.widget.indicator.field.description')}
          value={(config.description as string) ?? ''}
          onChange={(e) => handleChange('description', e.target.value)}
        />
      </div>

      <h5>{t('dashboard.widget.indicator.section.valueFormatting')}</h5>

      <PresetTextField
        label="dashboard.widget.indicator.field.prefix"
        value={(config.prefix as string) ?? ''}
        onChange={(v) => handleChange('prefix', v)}
        testId="indicator-settings-prefix"
        testIdPrefix="indicator-settings-prefix"
      />

      <PresetTextField
        label="dashboard.widget.indicator.field.suffix"
        value={(config.suffix as string) ?? ''}
        onChange={(v) => handleChange('suffix', v)}
        testId="indicator-settings-suffix"
        testIdPrefix="indicator-settings-suffix"
      />

      <div className="mb-3">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            data-testid="indicator-settings-use-millions"
            id="indicator-settings-use-millions"
            checked={!!config.useMillions}
            onChange={(e) => handleChange('useMillions', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="indicator-settings-use-millions">
            {t('dashboard.widget.indicator.field.useMillions')}
          </label>
        </div>
      </div>

      {!!config.useMillions && (
        <div className="mb-3">
          <label className="form-label">{t('dashboard.widget.indicator.field.millionsFrom')}</label>
          <input
            type="number"
            className="form-control"
            data-testid="indicator-settings-millions-from"
            value={numberValue('millionsFrom')}
            onChange={(e) => handleChange('millionsFrom', e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">{t('dashboard.widget.indicator.field.decimalPlaces')}</label>
        <input
          type="number"
          className="form-control"
          data-testid="indicator-settings-decimal-places"
          value={numberValue('decimalPlaces')}
          onChange={(e) => handleChange('decimalPlaces', e.target.value === '' ? null : Number(e.target.value))}
        />
      </div>

      <h5>{t('dashboard.widget.indicator.section.deltaFormatting')}</h5>

      <PresetTextField
        label="dashboard.widget.indicator.field.deltaPrefix"
        value={(config.deltaPrefix as string) ?? ''}
        onChange={(v) => handleChange('deltaPrefix', v)}
        testId="indicator-settings-delta-prefix"
        testIdPrefix="indicator-settings-delta-prefix"
      />

      <PresetTextField
        label="dashboard.widget.indicator.field.deltaSuffix"
        value={(config.deltaSuffix as string) ?? ''}
        onChange={(v) => handleChange('deltaSuffix', v)}
        testId="indicator-settings-delta-suffix"
        testIdPrefix="indicator-settings-delta-suffix"
      />

      <div className="mb-3">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            data-testid="indicator-settings-use-millions-delta"
            id="indicator-settings-use-millions-delta"
            checked={!!config.useMillionsDelta}
            onChange={(e) => handleChange('useMillionsDelta', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="indicator-settings-use-millions-delta">
            {t('dashboard.widget.indicator.field.useMillionsDelta')}
          </label>
        </div>
      </div>

      {!!config.useMillionsDelta && (
        <div className="mb-3">
          <label className="form-label">{t('dashboard.widget.indicator.field.millionsFromDelta')}</label>
          <input
            type="number"
            className="form-control"
            data-testid="indicator-settings-millions-from-delta"
            value={numberValue('millionsFromDelta')}
            onChange={(e) => handleChange('millionsFromDelta', e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">{t('dashboard.widget.indicator.field.decimalPlacesDelta')}</label>
        <input
          type="number"
          className="form-control"
          data-testid="indicator-settings-decimal-places-delta"
          value={numberValue('decimalPlacesDelta')}
          onChange={(e) => handleChange('decimalPlacesDelta', e.target.value === '' ? null : Number(e.target.value))}
        />
      </div>

      <h5>{t('dashboard.widget.indicator.section.visualSettings')}</h5>

      <div className="mb-3" data-testid="indicator-settings-icon">
        <label className="form-label">{t('dashboard.widget.indicator.field.icon')}</label>
        <IconPicker
          value={(config.icon as string) ?? ''}
          onChange={(iconClass) => handleChange('icon', iconClass)}
        />
      </div>

      <div className="mb-3" data-testid="indicator-settings-color">
        <label className="form-label">{t('dashboard.widget.indicator.field.color')}</label>
        <ColorPicker
          value={(config.color as string) ?? '#3a57e8'}
          onChange={(color) => handleChange('color', color)}
        />
      </div>

      <div className="mb-3" data-testid="indicator-settings-bg-color">
        <label className="form-label">{t('dashboard.widget.indicator.field.bgColor')}</label>
        <ColorPicker
          value={(config.bgColor as string) ?? '#3a57e8'}
          onChange={(color) => handleChange('bgColor', color)}
        />
      </div>

      <div className="mb-3" data-testid="indicator-settings-delta-color-up">
        <label className="form-label">{t('dashboard.widget.indicator.field.deltaColorUp')}</label>
        <ColorPicker
          value={(config.deltaColorUp as string) ?? '#1aa053'}
          onChange={(color) => handleChange('deltaColorUp', color)}
        />
      </div>

      <div className="mb-3" data-testid="indicator-settings-delta-color-down">
        <label className="form-label">{t('dashboard.widget.indicator.field.deltaColorDown')}</label>
        <ColorPicker
          value={(config.deltaColorDown as string) ?? '#c03221'}
          onChange={(color) => handleChange('deltaColorDown', color)}
        />
      </div>

      <div className="mb-3" data-testid="indicator-settings-delta-color-neutral">
        <label className="form-label">{t('dashboard.widget.indicator.field.deltaColorNeutral')}</label>
        <ColorPicker
          value={(config.deltaColorNeutral as string) ?? '#6c757d'}
          onChange={(color) => handleChange('deltaColorNeutral', color)}
        />
      </div>
    </div>
  );
}
